import type { Comment } from '@kizen/api/comment';
import type {
  CustomerNotification,
  CustomerNotificatonBulkAction,
  CustomerNotificationFilter,
  CustomerNotificationParams,
  CustomerNotificationMetadata,
  PaginatedCustomerNotificationList,
} from '@kizen/api/customer-notifications';
import type { QueryDataOf } from '@kizen/query';
import type { PropsWithChildren } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import type { Store } from 'store/types';

import {
  archiveCustomerNotifications,
  markCustomerNotificationsRead,
} from '@kizen/api/customer-notifications';
import { updateEmployeePageConfig } from '@kizen/api/employee';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  FROM_NOTIFICATION_KEY,
  VIEW_NOTIFICATION_COMMENTS_KEY,
} from 'constants/searchParams';
import { toastVariant, useToast } from '__components/ToastProvider';
import useSearchParam, { setSearchParams } from 'hooks/useSearchParam';
import { monitoringMessageHelper } from 'instrumentation/helpers';
import {
  customerNotificationsIndicatorQuery,
  customerNotificationsInfiniteQuery,
  customerNotificationsMetadataQuery,
} from 'query/customer-notifications';
import { employeePageConfigQuery } from 'query/employee';
import AxiosService from 'services/AxiosService';
import ActivityService from 'services/ActivityService';

export type Action =
  | PaginatedCustomerNotificationList['results'][number]['actions'][number]
  | { type: 'mark_read' | 'mark_unread' | 'archive' | 'unarchive' };

export type BulkAction = Exclude<BulkActionState['action'], null>;

type BulkActionState = {
  action: CustomerNotificatonBulkAction['action'] | null;
  open: boolean;
};

type Column = CustomerNotificationMetadata['table']['columns'][number];

type Filter =
  | CustomerNotificationMetadata['filters'][number]
  | {
      input_type: 'button_radio';
      name: 'archive_status';
      options: Option<'archived' | 'all' | null>[];
    };

type FilterOptionValue<T extends CustomerNotificationFilter['name']> =
  CustomerNotificationFilter<T> extends {
    options: { value: any }[];
  }
    ? CustomerNotificationFilter<T>['options'][number]['value']
    : never;

export type FilterState = {
  archive_status: 'archived' | 'all' | null;
  comments: FilterOptionValue<'comments'> | null;
  created: [string, string] | null;
  notification_type: Option<FilterOptionValue<'notification_type'>>[];
  read_status: FilterOptionValue<'read_status'> | null;
  scope: FilterOptionValue<'scope'> | null;
  status: Option<FilterOptionValue<'status'>>[];
};

type Filters = {
  [F in Filter as F['name']]: F;
};

type NotificationsPageValue = ReturnType<typeof useNotifications>;

type NotificationsQueryData = InfiniteData<
  PaginatedCustomerNotificationList,
  string | undefined
>;

export type Option<T = string> = {
  label: string;
  value: T;
};

export type TableState = {
  group: 'all' | 'none' | 'search';
  checked: Set<string>;
  search: string;
};

const INDICATOR_QUERY_KEY = customerNotificationsIndicatorQuery().queryKey;
const PAGE_CONFIG_QUERY_KEY = employeePageConfigQuery(
  'notification_center'
).queryKey;

const NotificationsPageContext = createContext<
  NotificationsPageValue | undefined
>(undefined);

const InitialFilterState: FilterState = {
  archive_status: null,
  created: null,
  comments: null,
  notification_type: [],
  read_status: null,
  scope: null,
  status: [],
};

const useNotifications = () => {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const history = useHistory();
  const [showToast] = useToast();
  const notificationId = useSearchParam(FROM_NOTIFICATION_KEY);
  const viewComments = useSearchParam(VIEW_NOTIFICATION_COMMENTS_KEY);
  const userId = useSelector((s: Store) => s.authentication.teamMember.id);
  const scrollRef = useRef<HTMLDivElement>();
  const previousData = useRef<NotificationsQueryData>();
  const urlNotificationCommentModalOpened = useRef(false);

  const [enableQuery, setEnableQuery] = useState(false);

  const [filters, setFilters] = useState<FilterState>(InitialFilterState);

  const [state, setState] = useState<TableState>({
    checked: new Set(),
    group: 'none',
    search: '',
  });

  const [bulkAction, setBulkAction] = useState<BulkActionState>({
    action: null,
    open: false,
  });

  const [commenting, setCommenting] = useState<CustomerNotification | null>(
    null
  );

  useEffect(() => {
    return () => {
      qc.removeQueries({ queryKey: PAGE_CONFIG_QUERY_KEY });
    };
  }, [qc]);

  const { checked, group, search } = state;
  const {
    archive_status,
    comments,
    created,
    notification_type,
    read_status,
    scope,
    status,
  } = filters;
  const showFolderColumn = archive_status === 'all';

  const params = useMemo(() => {
    const result: CustomerNotificationParams = { size: 50 };

    if (notificationId) {
      result.notification_id = notificationId;
    }

    if (scope) {
      result.scope = scope;
    }

    if (read_status) {
      result.read = read_status === 'read';
    }

    if (created) {
      result.date_from = created[0];
      result.date_to = created[1];
    }

    if (archive_status === 'archived') {
      result.archived = true;
    } else if (archive_status === null) {
      result.archived = false;
    }

    if (notification_type.length) {
      result.notification_type = notification_type.map((opt) => opt.value);
    }

    if (status.length) {
      result.status = status.map((s) => s.value);
    }

    if (comments === 'has_comments') {
      result.has_comments = true;
    } else if (comments === 'is_me') {
      result.mentions = userId;
    }

    if (search) {
      result.search = search;
    }

    return result;
  }, [
    archive_status,
    comments,
    created,
    notification_type,
    notificationId,
    read_status,
    scope,
    search,
    status,
    userId,
  ]);

  const metadataQuery = useQuery(
    customerNotificationsMetadataQuery({
      staleTime: Infinity,
      select: (data) => {
        let notificationTypeOptions: Filters['notification_type']['options'] =
          [];
        let statusOptions: Filters['status']['options'] = [];

        for (const filter of data.filters) {
          if (filter.name === 'notification_type') {
            notificationTypeOptions = filter.options;
          }

          if (filter.name === 'status') {
            statusOptions = filter.options;
          }
        }

        const columns = showFolderColumn
          ? data.table.columns
          : data.table.columns.filter((column) => column !== 'folder');

        const entries = Object.entries(data.table.widths) as Array<
          [Column, string]
        >;

        const widths = entries.reduce(
          (acc, [column, width]) => {
            if (column === 'title' && showFolderColumn) {
              acc[column] =
                `calc(${data.table.widths.title} - ${data.table.widths.folder} - 30px)`;
            } else {
              acc[column] = width;
            }

            return acc;
          },
          {} as Record<Column, string>
        );

        return {
          ...data,
          table: { ...data.table, columns, widths },
          notificationTypeOptions,
          statusOptions,
        };
      },
    })
  );

  const configQuery = useQuery(employeePageConfigQuery('notification_center'));

  const queryOptions = useMemo(() => {
    return customerNotificationsInfiniteQuery({
      params,
      enabled: enableQuery,
      placeholderData: keepPreviousData,
      staleTime: 0,
    });
  }, [enableQuery, params]);

  const { queryKey } = queryOptions;

  const query = useInfiniteQuery(queryOptions);

  const { data, isFetching, refetch } = query;

  const visibleData = !data && isFetching ? previousData.current : data;

  if (!configQuery.isLoading && metadataQuery.data && !enableQuery) {
    // Make the initial notifications request using the page config values
    setEnableQuery(true);

    const config = configQuery.data?.config;

    if (config) {
      const search = config.search;

      if (search) {
        setState((prev) => {
          return { ...prev, search };
        });
      }

      setFilters((prev) => {
        const { archived, created, notification_type, scope, status } = config;
        const next = { ...prev };

        if ('archived' in config) {
          next.archive_status = archived ?? null;
        }

        if (created) {
          next.created = created;
        }

        if (notification_type) {
          next.notification_type =
            metadataQuery.data.notificationTypeOptions.filter((opt) => {
              return notification_type.includes(opt.value);
            });
        }

        if ('read' in config) {
          next.read_status = config.read ? 'read' : 'unread';
        }

        if (scope) {
          next.scope = scope;
        }

        if (status) {
          next.status = metadataQuery.data.statusOptions.filter((opt) => {
            return status.includes(opt.value);
          });
        }

        if (config.has_comments) {
          next.comments = 'has_comments';
        } else if (config.mentions === 'is_me') {
          next.comments = 'is_me';
        }

        return next;
      });
    }
  }

  if (data !== previousData.current && data && group !== 'none') {
    // Load new pages as checked when 'All in Search' or 'All Notifications' is selected
    for (const notification of data.pages.flatMap((page) => page.results)) {
      checked.add(notification.id);
    }
  }

  if (data) {
    previousData.current = data;
  }

  const notifications =
    visibleData?.pages.flatMap((page) => page.results) ?? [];

  const ops = useMemo(() => {
    const scrollToTop = (options: Pick<ScrollToOptions, 'behavior'> = {}) => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ ...options, top: 0 });
      }
    };

    const clearFilters = () => {
      scrollToTop();
      setFilters(InitialFilterState);
      setState((prev) => {
        prev.checked.clear();

        return { ...prev, group: 'none' };
      });
      updateEmployeePageConfig(AxiosService, 'notification_center', {
        config: {},
      });
    };

    const onOpenCommentModal = (notification: CustomerNotification) => {
      setCommenting(notification);
      qc.setQueryData<QueryDataOf<typeof queryOptions>>(queryKey, (data) => {
        if (data) {
          const queriedNotification = data.pages
            .flatMap((page) => page.results)
            .find((cn) => cn.id === notification.id);

          if (queriedNotification) {
            queriedNotification.read = true;
          }
        }

        return data;
      });
    };

    const onHideCommentModal = () => {
      setCommenting(null);
    };

    const onSaveComment = (notificationId: string, _comment: Comment) => {
      qc.setQueryData<QueryDataOf<typeof queryOptions>>(queryKey, (data) => {
        if (data) {
          const notification = data.pages
            .flatMap((page) => page.results)
            .find((cn) => cn.id === notificationId);

          if (notification) {
            notification.comment_count += 1;
          }
        }

        return data;
      });
    };

    const onFilterChange = (filter: Pick<Filter, 'name'>, value: any) => {
      scrollToTop();
      setFilters((prev) => {
        return { ...prev, [filter.name]: value };
      });
      setState((prev) => {
        prev.checked.clear();

        return { ...prev, group: 'none' };
      });
    };

    const onRefresh = async () => {
      if (notificationId) {
        scrollToTop();
        setSearchParams(
          history,
          {
            [FROM_NOTIFICATION_KEY]: null,
            [VIEW_NOTIFICATION_COMMENTS_KEY]: null,
          },
          { method: 'replace' }
        );
      } else {
        scrollToTop({ behavior: 'smooth' });
      }

      setState((prev) => {
        prev.checked.clear();

        return { ...prev, group: 'none' };
      });

      await refetch();
      qc.invalidateQueries({ queryKey: INDICATOR_QUERY_KEY });
    };

    const onSearch = (search: string) => {
      scrollToTop();
      setState((prev) => {
        prev.checked.clear();

        return { ...prev, group: 'none', search };
      });
    };

    const onCheckNotification = (notification: CustomerNotification) => {
      setState(({ checked, ...rest }) => {
        if (checked.has(notification.id)) {
          checked.delete(notification.id);
        } else {
          checked.add(notification.id);
        }

        return { checked, ...rest };
      });
    };

    const onAllCheckedChange = (options: {
      group: TableState['group'];
      notifications?: CustomerNotification[];
    }) => {
      const { group, notifications = [] } = options;

      setState((prev) => {
        prev.checked.clear();

        for (const notification of notifications) {
          prev.checked.add(notification.id);
        }

        return { ...prev, group };
      });
    };

    const onSelectBulkAction = (action: BulkActionState['action']) => {
      setBulkAction((prev) => {
        return { ...prev, action, open: true };
      });
    };

    const showBulkActionModal = () => {
      setBulkAction((prev) => {
        return { ...prev, open: true };
      });
    };

    const hideBulkActionModal = () => {
      setBulkAction({ action: null, open: false });
    };

    const performBulkAction = async (options: {
      action: BulkAction;
      count: number;
      key: string;
    }) => {
      const { action, count, key } = options;

      const text =
        count === 1 ? t('notification has') : t('notifications have');
      const messages: Record<BulkAction, string> = {
        notifications_archive: t(
          '{{count}} {{text}} been queued to be archived.',
          { count, text }
        ),
        notifications_unarchive: t(
          '{{count}} {{text}} been queued to be unarchived.',
          { count, text }
        ),
        notifications_mark_as_read: t(
          '{{count}} {{text}} been queued to be read.',
          { count, text }
        ),
        notifications_mark_as_unread: t(
          '{{count}} {{text}} been queued to be unread.',
          { count, text }
        ),
      };

      if (action === 'notifications_archive') {
        await archiveCustomerNotifications(AxiosService, {
          archived: true,
          summary_key: key,
        });
      } else if (action === 'notifications_unarchive') {
        await archiveCustomerNotifications(AxiosService, {
          archived: false,
          summary_key: key,
        });
      } else if (action === 'notifications_mark_as_read') {
        await markCustomerNotificationsRead(AxiosService, {
          read: true,
          summary_key: key,
        });
      } else if (action === 'notifications_mark_as_unread') {
        await markCustomerNotificationsRead(AxiosService, {
          read: false,
          summary_key: key,
        });
      }

      if (messages[action]) {
        showToast({ message: messages[action] });
      }

      hideBulkActionModal();
    };

    const performAction = async (
      action: Action,
      notification: CustomerNotification
    ) => {
      if (action.type === 'archive') {
        try {
          await archiveCustomerNotifications(AxiosService, {
            notification_ids: [notification.id],
            archived: true,
          });

          showToast({
            message: t('Notification Archived'),
            variant: toastVariant.SUCCESS,
          });

          qc.invalidateQueries({ queryKey: INDICATOR_QUERY_KEY });
        } catch (error) {
          showToast({
            message: t('Archive failed'),
            variant: toastVariant.FAILURE,
          });
          return;
        }
      } else if (action.type === 'unarchive') {
        try {
          await archiveCustomerNotifications(AxiosService, {
            notification_ids: [notification.id],
            archived: false,
          });

          showToast({
            message: t('Notification Unarchived'),
            variant: toastVariant.SUCCESS,
          });

          qc.invalidateQueries({ queryKey: INDICATOR_QUERY_KEY });
        } catch (error) {
          showToast({
            message: t('Unarchive failed'),
            variant: toastVariant.FAILURE,
          });
          return;
        }
      } else if (action.type === 'mark_read') {
        try {
          await markCustomerNotificationsRead(AxiosService, {
            notification_ids: [notification.id],
            read: true,
          });

          showToast({
            message: t('Notification Marked as Read'),
            variant: toastVariant.SUCCESS,
          });

          qc.invalidateQueries({ queryKey: INDICATOR_QUERY_KEY });
        } catch (error) {
          showToast({
            message: t('Mark as read failed'),
            variant: toastVariant.FAILURE,
          });
          return;
        }
      } else if (action.type === 'mark_unread') {
        try {
          await markCustomerNotificationsRead(AxiosService, {
            notification_ids: [notification.id],
            read: false,
          });

          showToast({
            message: t('Notification Marked as Unread'),
            variant: toastVariant.SUCCESS,
          });

          qc.invalidateQueries({ queryKey: INDICATOR_QUERY_KEY });
        } catch (error) {
          showToast({
            message: t('Mark as unread failed'),
            variant: toastVariant.FAILURE,
          });
          return;
        }
      } else if (action.type === 'resend_webhook') {
        try {
          await ActivityService.v2ResendWebhook({
            activityId: action.activity_id,
            loggedActivityId: action.logged_activity_id,
          });

          showToast({
            message: t('Webhook Resent'),
            variant: toastVariant.SUCCESS,
          });
        } catch (error) {
          showToast({
            message: t('Webhook resend failed'),
            variant: toastVariant.FAILURE,
          });
          return;
        }
      } else if (action.type === 'view_automation') {
        window.open(
          `/agentic-workflow/${action.automation_id}`,
          '_blank',
          'noopener,noreferrer'
        );
      } else if (action.type === 'view_broadcast') {
        window.open('/broadcasts', '_blank', 'noopener,noreferrer');
      } else {
        if ('url' in action) {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        } else {
          monitoringMessageHelper(
            `Unhandled notification action of type ${action.type}.`,
            { extra: { action, notification } }
          );
        }
      }

      if (
        !notification.read &&
        action.type !== 'archive' &&
        action.type !== 'unarchive' &&
        action.type !== 'mark_read' &&
        action.type !== 'mark_unread'
      ) {
        await markCustomerNotificationsRead(AxiosService, {
          notification_ids: [notification.id],
          read: true,
        });
      }

      refetch();
    };

    return {
      clearFilters,
      hideBulkActionModal,
      onAllCheckedChange,
      onCheckNotification,
      onFilterChange,
      onOpenCommentModal,
      onHideCommentModal,
      onRefresh,
      onSaveComment,
      onSearch,
      onSelectBulkAction,
      performAction,
      performBulkAction,
      showBulkActionModal,
    };
  }, [notificationId, queryKey, history, t, qc, showToast, refetch]);

  if (
    viewComments === 'true' &&
    notificationId &&
    !query.isLoading &&
    !urlNotificationCommentModalOpened.current &&
    data
  ) {
    const notification = data.pages[0].results.find(
      (n) => n.id === notificationId
    );

    if (notification) {
      setCommenting(notification);
      urlNotificationCommentModalOpened.current = true;
    }
  }

  return {
    ...state,
    ...ops,
    bulkAction,
    filters,
    notifications,
    commenting,
    params,
    query,
    metadata: metadataQuery.data!,
    scrollRef,
  };
};

export const useNotificationsPage = (): NotificationsPageValue => {
  const ctx = useContext(NotificationsPageContext);

  if (!ctx) {
    throw Error(
      'useNotificationsPage must be used within NotificationsPageProvider'
    );
  }

  return ctx;
};

export const NotificationsPageProvider = ({ children }: PropsWithChildren) => {
  const ctx = useNotifications();

  return (
    <NotificationsPageContext.Provider value={ctx}>
      {children}
    </NotificationsPageContext.Provider>
  );
};
